import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Search, ImageIcon, Check, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Variant, ProductImage } from './VariantForm';

interface ProductSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  refreshKey?: number;
  onAddProduct?: () => void;
  onConfirm?: (items: Array<{ id: string; nombre: string; thumbnail: string | null }>) => void;
}

export const ProductSelectorModal: React.FC<ProductSelectorModalProps> = ({ isOpen, onClose, refreshKey = 0, onAddProduct, onConfirm }) => {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [signedUrls, setSignedUrls] = useState<Record<string, { full: string; thumbnail: string }>>({});

  const cardRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingImageKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) return;
    void fetchVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, refreshKey]);

  const fetchVariants = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company, company_id')
        .eq('id', user.id)
        .single();

      let compId = (profile as any)?.id_company as string | null;
      if (!compId && (profile as any)?.company_id) {
        const { data: comp } = await supabase
          .from('company_profiles')
          .select('id_company')
          .eq('id', (profile as any).company_id)
          .single();
        compId = (comp as any)?.id_company ?? null;
      }
      if (!compId) throw new Error('No company associated with user');
      setCompanyId(compId);

      const { data: variantsData, error: variantsError } = await supabase
        .from('variantes')
        .select('id, nombre, created_at')
        .eq('activo', true)
        .eq('company_id', compId)
        .order('created_at', { ascending: false });

      if (variantsError) throw variantsError;

      let processed: Variant[] = [];
      if (variantsData && variantsData.length > 0) {
        const variantIds = variantsData.map(v => v.id as string);
        const { data: imagesData, error: imagesError } = await supabase
          .from('productos_imagenes')
          .select('id, url, principal, orden, variante_id')
          .eq('company_id', compId)
          .in('variante_id', variantIds);

        if (imagesError) throw imagesError;

        const imagesByVariantId: Record<string, ProductImage[]> = {};
        (imagesData || []).forEach((image: any) => {
          const variantId = image.variante_id as string;
          const entry: ProductImage = {
            id: image.id,
            url: image.url,
            principal: image.principal,
            orden: image.orden,
            producto_variante_id: 0,
            variante_id: variantId,
          };
          if (!imagesByVariantId[variantId]) imagesByVariantId[variantId] = [];
          imagesByVariantId[variantId].push(entry);
        });

        processed = variantsData.map((v: any) => ({ id: v.id as string, nombre: v.nombre, images: imagesByVariantId[v.id as string] || [] }));
      }

      setVariants(processed);
      setSignedUrls({});
      setSelected([]);
    } catch (err) {
      console.error('Error fetching variants (modal):', err);
      setError(err instanceof Error ? err.message : 'Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const principalImageUrl = (variant: Variant): string | null => {
    const img = (variant.images || []).find(i => i.principal) || (variant.images || [])[0];
    if (!img?.url) return null;
    const urlKey = img.url;
    if (urlKey.startsWith('http')) return urlKey;
    const signed = signedUrls[urlKey];
    return signed?.thumbnail || null;
  };

  const ensurePrincipalImageSigned = async (variantId: string) => {
    const variant = variants.find(v => v.id === variantId);
    if (!variant) return;
    const img = (variant.images || []).find(i => i.principal) || (variant.images || [])[0];
    if (!img?.url) return;
    const key = img.url as string;
    if (key.startsWith('http')) {
      if (!signedUrls[key]) {
        setSignedUrls(prev => ({ ...prev, [key]: { full: key, thumbnail: key } }));
      }
      return;
    }
    if (!companyId) return;
    if (signedUrls[key] || loadingImageKeysRef.current.has(key)) return;

    loadingImageKeysRef.current.add(key);
    try {
      const pathVariantId = (img as any)?.variante_id ?? variant.id;
      const optFilePath = `${companyId}/${pathVariantId}/opt/${key}`;
      const fullFilePath = `${companyId}/${pathVariantId}/full/${key}`;

      let thumbSigned: string | null = null;
      let fullSigned: string | null = null;

      const { data: thumb } = await supabase.storage.from('productos').createSignedUrl(optFilePath, 3600);
      if (thumb?.signedUrl) {
        thumbSigned = thumb.signedUrl;
        const { data: full } = await supabase.storage.from('productos').createSignedUrl(fullFilePath, 3600);
        fullSigned = full?.signedUrl || thumbSigned;
      } else {
        const { data: full } = await supabase.storage.from('productos').createSignedUrl(fullFilePath, 3600);
        if (full?.signedUrl) {
          thumbSigned = full.signedUrl;
          fullSigned = full.signedUrl;
        }
      }

      setSignedUrls(prev => ({ ...prev, [key]: { full: fullSigned || key, thumbnail: thumbSigned || fullSigned || key } }));
    } catch (e) {
      setSignedUrls(prev => ({ ...prev, [key]: { full: 'https://via.placeholder.com/300x300?text=Error', thumbnail: 'https://via.placeholder.com/300x300?text=Error' } }));
    } finally {
      loadingImageKeysRef.current.delete(key);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target as HTMLElement;
          const idAttr = el.getAttribute('data-variant-id');
          const vid = idAttr || '';
          if (vid) {
            void ensurePrincipalImageSigned(vid as string);
          }
          observerRef.current?.unobserve(entry.target);
        }
      });
    }, { root: null, rootMargin: '200px 0px', threshold: 0.01 });

    const vs = filteredVariants;
    vs.forEach((v) => {
      const el = cardRefs.current[String(v.id)];
      if (el) observerRef.current!.observe(el);
    });

    return () => observerRef.current?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, variants, companyId, query]);

  const filteredVariants = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return variants;
    return variants.filter(v => (v.nombre || '').toLowerCase().includes(q));
  }, [variants, query]);

  const toggleSelect = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-sm max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Seleccioná productos</h2>
          <button onClick={onClose} className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-4">
          <label htmlFor="search-productos-modal" className="sr-only">Buscar productos</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              id="search-productos-modal"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar productos..."
              className="w-full pl-10 pr-3 h-11 sm:h-12 rounded-2xl bg-gray-50 sm:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm placeholder:text-gray-400"
            />
          </div>
        </div>

        {error && (
          <div className="px-4 sm:px-6 mb-2 text-sm text-red-600">{error}</div>
        )}

        {loading ? (
          <div className="px-4 sm:px-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-md overflow-hidden">
                <div className="w-full aspect-square bg-gray-100 animate-pulse" />
                <div className="p-3">
                  <div className="h-4 bg-gray-100 rounded w-2/3 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 sm:px-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Add product card */}
            <button
              type="button"
              onClick={() => {
                if (onAddProduct) onAddProduct();
              }}
              className="relative text-left bg-white border-0 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 group"
            >
              <div className="relative pt-0 px-0 sm:pt-3 sm:px-3">
                <div className="w-full aspect-square bg-white flex items-center justify-center rounded-b-2xl sm:rounded-lg">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-blue-500 text-white flex items-center justify-center transition-colors group-hover:bg-blue-600">
                    <Plus className="w-8 h-8 sm:w-9 sm:h-9" />
                  </div>
                </div>
              </div>
              <div className="p-3">
                <p className="text-sm text-gray-900 truncate font-medium py-1">Agregar producto</p>
              </div>
            </button>

            {/* Variants */}
            {filteredVariants.length > 0 && filteredVariants.map(variant => {
              const imgUrl = principalImageUrl(variant);
              const isSelected = selected.includes(variant.id);
              return (
                <button
                  key={variant.id}
                  type="button"
                  onClick={() => toggleSelect(variant.id)}
                  className={`relative text-left bg-white border-0 ${isSelected ? 'ring-2 ring-blue-500' : ''} rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 group`}
                  ref={(el) => { cardRefs.current[variant.id] = el; }}
                  data-variant-id={variant.id}
                >
                  <div className="relative pt-0 px-0 sm:pt-3 sm:px-3">
                    {imgUrl ? (
                      <img src={imgUrl} alt={variant.nombre} loading="lazy" className="w-full aspect-square object-cover rounded-b-2xl sm:rounded-lg" />
                    ) : (
                      <div className="w-full aspect-square bg-gray-100 flex items-center justify-center rounded-b-2xl sm:rounded-lg">
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 w-7 h-7 rounded-full ${isSelected ? 'bg-blue-500 text-white' : 'bg-white/80 text-gray-300 group-hover:text-gray-400 backdrop-blur-sm'} flex items-center justify-center shadow-none transition-colors`}>
                      <Check className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm text-gray-900 truncate font-medium py-1">{variant.nombre}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Sticky bottom confirm */}
        {selected.length > 0 && (
          <div className="sticky bottom-0 z-10 px-4 sm:px-6 py-3 flex justify-end sm:bg-white sm:border-t sm:border-gray-200">
            <button
              type="button"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 h-10 bg-blue-500 text-white rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.35)] sm:shadow-sm hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-lg sm:text-sm whitespace-nowrap"
              onClick={async () => {
                // Ensure thumbnails signed for selected
                await Promise.all(selected.map(async (id) => ensurePrincipalImageSigned(id)));
                const payload = selected.map((id) => {
                  const variant = variants.find(v => v.id === id)!;
                  const thumb = principalImageUrl(variant);
                  return { id, nombre: variant.nombre, thumbnail: thumb };
                });
                if (onConfirm) onConfirm(payload);
                onClose();
              }}
            >
              {`Seleccionar ${selected.length} ${selected.length === 1 ? 'producto' : 'productos'}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
;

export default ProductSelectorModal;
